import Button from "@components/Button";
import CustomerAvatar from "@common/components/CustomerAvatar";
import { Event as HotjarEvent, hotjar } from "@modules/core/hotjar/types";
import { User } from "@modules/user/types/user";
import { useTranslation } from "next-i18next";
import useWindowDimensions from "@common/hooks/useWindowDimensions";
import { sizeClass } from "@modules/cms/types/SizeClass";
import { TranslationKeys } from "src/@types/i18next";
import useCustomerInfoFromQuery from "@modules/query-string/hooks/useCustomerInfoFromQuery";
import LoginModalContext from "@modules/user/LoginModalContext";
import { useContext } from "react";
import { ArrowRightIcon } from "./icons";

type Props = {
  buttonText: TranslationKeys["common"];
  user?: User;
  onLoginClick: () => void;
  onProfileAvatarClick: () => void;
};

const CollapsibleLoginButton = ({
  buttonText,
  user,
  onLoginClick,
  onProfileAvatarClick,
}: Props) => {
  const { t } = useTranslation(["common"]);
  const { width } = useWindowDimensions();
  const shouldCollapse = width < sizeClass.md;

  const customerInfo = useCustomerInfoFromQuery();
  const { setDefaultEmailAddress, setDefaultFirstName, setDefaultLastName } =
    useContext(LoginModalContext);

  return (
    <div>
      {user && user.isLoggedIn ? (
        <Button
          fill="link"
          spacing="tight-hug"
          className="md:border border-gray-50 px-[.1rem] md:px-2"
          rightIcon={shouldCollapse ? undefined : <ArrowRightIcon />}
          onClick={onProfileAvatarClick}
        >
          <div className="flex items-center text-black max-w-[10rem] xl:max-w-[14rem]">
            <CustomerAvatar
              customer={user}
              size={shouldCollapse ? "xs" : "sm"}
              isCurrentUser
              isProfileAvatar
            />
            <p className="text-ellipsis whitespace-nowrap overflow-hidden pl-3 hidden md:inline">
              <span>{user.firstName}</span>{" "}
              <span data-hj-suppress>{user.lastName}</span>
            </p>
          </div>
        </Button>
      ) : (
        <Button
          onClick={() => {
            hotjar.event(HotjarEvent.UserLogin);
            setDefaultEmailAddress?.(customerInfo.email ?? "");
            setDefaultFirstName?.(customerInfo.firstName ?? "");
            setDefaultLastName?.(customerInfo.lastName ?? "");
            onLoginClick();
          }}
          rightIcon={shouldCollapse ? undefined : <ArrowRightIcon />}
          fill="outline"
          buttonStyle="primary"
          spacing={shouldCollapse ? "tight-hug" : "hug"}
          size={shouldCollapse ? "small" : "large"}
          className="text-primary-500 focus:outline-none focus-visible:shadow-elevation-03"
        >
          <p className="text-ellipsis whitespace-nowrap overflow-hidden">
            {t(buttonText)}
          </p>
        </Button>
      )}
    </div>
  );
};

export default CollapsibleLoginButton;
